import React, { useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, XCircle, FileSpreadsheet } from "lucide-react";
import { fmtDate } from "@/lib/api";

const STATUS_STYLE = {
  ok: "bg-white",
  warning: "bg-amber-50",
  error: "bg-rose-50",
};

const fmtTL = (v) => (v == null || v === "" ? "-" : Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

function Cell({ v, wide }) {
  return (
    <td className={`px-2 py-1.5 text-slate-700 ${wide ? "max-w-[160px]" : "max-w-[110px]"}`}>
      <span className="block truncate" title={v || ""}>{v || "-"}</span>
    </td>
  );
}

function Counter({ icon: Icon, n, label, tone, active, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${active ? "ring-2 ring-slate-900/20" : ""} ${tone}`}>
      <Icon size={13} /> {n} {label}
    </button>
  );
}

export function ImportPreviewModal({ preview, onCancel, onConfirm }) {
  const [filter, setFilter] = useState("all");
  const rows = useMemo(() => preview.rows || [], [preview.rows]);
  const counts = useMemo(() => ({
    ok: rows.filter((r) => r.status === "ok").length,
    warning: rows.filter((r) => r.status === "warning").length,
    error: rows.filter((r) => r.status === "error").length,
  }), [rows]);
  const shown = filter === "all" ? rows : rows.filter((r) => r.status === filter);
  const importable = counts.ok + counts.warning;
  const toggle = (k) => setFilter((f) => (f === k ? "all" : k));

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="import-preview-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[85vh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2 flex-wrap">
          <FileSpreadsheet size={18} className="text-indigo-600" />
          <h3 className="font-bold text-slate-900 mr-auto">İçe Aktarma Önizlemesi · {preview.total_rows} satır</h3>
          <Counter icon={CheckCircle2} n={counts.ok} label="sorunsuz" tone="bg-emerald-50 border-emerald-200 text-emerald-800" active={filter === "ok"} onClick={() => toggle("ok")} testid="import-preview-count-ok" />
          <Counter icon={AlertCircle} n={counts.warning} label="uyarılı" tone="bg-amber-50 border-amber-200 text-amber-800" active={filter === "warning"} onClick={() => toggle("warning")} testid="import-preview-count-warning" />
          <Counter icon={XCircle} n={counts.error} label="aktarılamaz" tone="bg-rose-50 border-rose-200 text-rose-800" active={filter === "error"} onClick={() => toggle("error")} testid="import-preview-count-error" />
        </div>
        <div className="overflow-auto flex-1">
          <table className="w-full text-xs" data-testid="import-preview-table">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left text-slate-600">
                {["Satır", "Müvekkil", "İcra Föy", "Dosya No", "Müşteri", "Keşideci", "Ödemeyi Yapan", "Tutar", "Tarih", "Durum"].map((h) => (
                  <th key={h} className="px-2 py-2 font-bold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map((r) => (
                <tr key={r.row} className={STATUS_STYLE[r.status]} data-testid={`import-preview-row-${r.row}`}>
                  <td className="px-2 py-1.5 font-mono text-slate-500">{r.row}</td>
                  <Cell v={r.muvekkil} wide />
                  <Cell v={r.icra_foy} />
                  <Cell v={r.dosya_no} />
                  <Cell v={r.musteri} wide />
                  <Cell v={r.kesideci} wide />
                  <Cell v={r.odemeyi_yapan_taraf} wide />
                  <td className="px-2 py-1.5 text-right font-mono-tab whitespace-nowrap">{fmtTL(r.tahsilat_miktari)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(r.tahsilat_tarihi) || "-"}</td>
                  <td className="px-2 py-1.5 min-w-[180px]">
                    {r.issues.length === 0
                      ? <span className="text-emerald-700 font-semibold">Sorunsuz</span>
                      : <span className={r.status === "error" ? "text-rose-700" : "text-amber-800"}>{r.issues.join(" · ")}</span>}
                  </td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr><td colSpan={10} className="text-center text-slate-400 py-6">Satır yok</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          {counts.error > 0 && <span className="text-xs text-rose-700 mr-auto">{counts.error} satır hatalı olduğu için aktarılmayacak.</span>}
          <button type="button" onClick={onCancel} data-testid="import-preview-cancel" className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm">İptal</button>
          <button type="button" onClick={onConfirm} disabled={importable === 0} data-testid="import-preview-confirm"
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-lg text-sm font-semibold">
            {importable} Satırı İçe Aktar
          </button>
        </div>
      </div>
    </div>
  );
}
